import { utcIsoToZonedParts } from '@eureca/ui';

/** `statusKey` do Core (o BFF deriva `expired` de published/private vencido). */
export type ProgramStatusKey =
  | 'not_published'
  | 'private'
  | 'published'
  | 'cancelled'
  | 'inactivated'
  | 'expired';

/**
 * EU-593 — modelo puro da tela "Gerenciar programa": rótulos, ordem canônica
 * das abas de fase, matriz de ações por status e formatação do prazo.
 * Sem hooks/API — testável isolado (BDD GPR-001..003, GPR-010, GPR-052).
 */

export type CandidateSubscriptionStatus =
  | 'active'
  | 'cancelled'
  | 'hired'
  | 'withdrawn'
  | 'closed'
  | 'rejected';

export type CandidatePhaseStatus = 'not_started' | 'in_progress' | 'completed';

/** Rótulos da coluna "Status inscrição" (Figma Management). */
export const SUBSCRIPTION_STATUS_LABEL: Record<CandidateSubscriptionStatus, string> = {
  active: 'Ativa',
  cancelled: 'Cancelada',
  hired: 'Contratada',
  withdrawn: 'Desistência',
  closed: 'Encerrada',
  rejected: 'Reprovada',
};

/** Opções do filtro "Status da inscrições" em ordem alfabética (GPR-039). */
export const SUBSCRIPTION_STATUS_FILTER_OPTIONS: {
  value: CandidateSubscriptionStatus;
  label: string;
}[] = [
  { value: 'active', label: 'Ativa' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'hired', label: 'Contratada' },
  { value: 'withdrawn', label: 'Desistência' },
  { value: 'closed', label: 'Encerrada' },
  { value: 'rejected', label: 'Reprovada' },
];

export const PHASE_STATUS_LABEL: Record<CandidatePhaseStatus, string> = {
  not_started: 'Não iniciada',
  in_progress: 'Em progresso',
  completed: 'Finalizada',
};

/**
 * Ordem canônica das abas de fase (Figma: Trilha, Jornada, Dinâmicas,
 * Entrevistas, Contratação). Só entram as fases que EXISTEM nas vagas do
 * programa (GPR-010/011) — `orderTabs` filtra por `availableStepTypeKeys`.
 */
export const STEP_TYPE_TAB_ORDER = [
  'online-trail',
  'deepening-journey',
  'dynamics',
  'interview',
  'hiring',
] as const;

export const STEP_TYPE_TAB_LABEL: Record<string, string> = {
  'online-trail': 'Trilha',
  'deepening-journey': 'Jornada',
  dynamics: 'Dinâmicas',
  interview: 'Entrevistas',
  hiring: 'Contratação',
};

export interface PhaseTab {
  /** `null` = aba "Todos". */
  stepTypeKey: string | null;
  label: string;
  count: number;
}

export function buildPhaseTabs(input: {
  availableStepTypeKeys: string[];
  tabs: { all: number; byStepType: { stepTypeKey: string; count: number }[] };
}): PhaseTab[] {
  const countByKey = new Map(input.tabs.byStepType.map((t) => [t.stepTypeKey, t.count]));
  const available = new Set(input.availableStepTypeKeys);
  const ordered = STEP_TYPE_TAB_ORDER.filter((key) => available.has(key));
  // Tipos fora da ordem canônica (futuro) entram no fim, em vez de sumirem.
  const extras = input.availableStepTypeKeys.filter(
    (key) => !STEP_TYPE_TAB_ORDER.includes(key as (typeof STEP_TYPE_TAB_ORDER)[number])
  );
  return [
    { stepTypeKey: null, label: 'Todos', count: input.tabs.all },
    ...[...ordered, ...extras].map((key) => ({
      stepTypeKey: key,
      label: STEP_TYPE_TAB_LABEL[key] ?? key,
      count: countByKey.get(key) ?? 0,
    })),
  ];
}

/** Ações da linha do card do programa, por status (GPR-001..003, EU-570). */
export type ProgramManagementAction =
  | 'edit'
  | 'view'
  | 'publish'
  | 'make-private'
  | 'make-public'
  | 'duplicate'
  | 'end'
  | 'cancel';

export interface ProgramActionsRow {
  /** Botão primário: Editar informações (editável) ou Conferir informações. */
  primary: 'edit' | 'view';
  /** Ícones na ordem do Figma. */
  icons: Exclude<ProgramManagementAction, 'edit' | 'view'>[];
}

export function actionsForStatus(status: ProgramStatusKey): ProgramActionsRow {
  switch (status) {
    case 'published':
      // GPR-001 — editar, privar, duplicar, encerrar, cancelar.
      return { primary: 'edit', icons: ['make-private', 'duplicate', 'end', 'cancel'] };
    case 'private':
      // GPR-002 — privar vira "tornar público".
      return { primary: 'edit', icons: ['make-public', 'duplicate', 'end', 'cancel'] };
    case 'not_published':
      // GPR-003 — lançar, duplicar, cancelar; sem privar nem encerrar.
      return { primary: 'edit', icons: ['publish', 'duplicate', 'cancel'] };
    case 'expired':
    case 'inactivated':
    case 'cancelled':
      // Encerrada/Cancelada: só conferir + duplicar (GPR-028/frames Closed/Canceled).
      return { primary: 'view', icons: ['duplicate'] };
  }
}

export const ACTION_TOOLTIP: Record<Exclude<ProgramManagementAction, 'edit' | 'view'>, string> = {
  publish: 'Lançar programa',
  'make-private': 'Privar programa',
  'make-public': 'Tornar programa público',
  duplicate: 'Duplicar programa',
  end: 'Encerrar programa',
  cancel: 'Cancelar programa',
};

const APP_TIMEZONE = 'America/Sao_Paulo';

function toBrParts(iso: string): { day: string; month: string; year: string } | null {
  try {
    const { date } = utcIsoToZonedParts(iso, APP_TIMEZONE);
    const [year, month, day] = date.split('-');
    if (!year || !month || !day) return null;
    return { day, month, year };
  } catch {
    return null;
  }
}

/**
 * "Prazo de inscrição: 24/06 até 24/07/2026" — início sem ano, fim com ano
 * (Figma). Instantes ancorados em São Paulo (superfície do consultor — regra
 * date-timezone-standard, exceção 2).
 */
export function formatApplicationWindow(
  startApplying: string | null | undefined,
  endApplying: string | null | undefined
): { start: string; end: string } | null {
  if (!startApplying || !endApplying) return null;
  const start = toBrParts(startApplying);
  const end = toBrParts(endApplying);
  if (!start || !end) return null;
  return {
    start: `${start.day}/${start.month}`,
    end: `${end.day}/${end.month}/${end.year}`,
  };
}

/** Data civil YYYY-MM-DD → DD/MM/YYYY literal (sem fuso — civil date). */
export function formatCivilDateBr(value: string | null | undefined): string | null {
  if (!value) return null;
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return null;
  return `${day}/${month}/${year}`;
}
